/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineComponent, ref, onMounted, computed } from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";

export default defineComponent({
  name: "c-input",

  props: {
    value: {
      type: [String, Number, Boolean, Date],
      default: "",
    },
    label: { type: String, default: "" },
    type: { type: String, default: "text" },
    placeholder: { type: String, default: "" },
    error: { type: [Boolean, String], default: false },
    showError: { type: [Boolean, String], default: true },
    lowercase: { type: Boolean, default: false },
    uppercase: { type: Boolean, default: false },
    number: { type: Boolean, default: false },
    integer: { type: Boolean, default: false },
    percentage: { type: Boolean, default: false },
    decimal: { type: Boolean, default: false },
    format: { type: [String, RegExp], default: "" },
    length: { type: [String, Number], default: null },
    not: { type: Object, default: () => ({}) },
    readonly: { type: Boolean, default: false },
    string: { type: Boolean, default: false },
    latLong: { type: Boolean, default: false },
    version: { type: Boolean, default: false },
    minDate: { type: [Date, String], default: "" },
    schema: { type: Function, default: () => ({}) },
    refIds: { type: String, default: "" },
    className: { type: String, default: "input" },
    searchable: { type: Boolean, default: false },
    eye: { type: Boolean, default: false },
    zero: { type: Boolean, default: false },
    space: { type: Boolean, default: false },
    qty: { type: Boolean, default: false },
    price: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    dateFormat: { type: String, default: "MM/dd/yyyy hh:mm a" },
    left: { type: Boolean, default: true },
  },

  emits: ["update:value", "enter"],

  components: {
    VueDatePicker,
  },

  setup(props, { emit }) {
    const showPopup = ref(false);
    const autocompleteValue = "off";
    const isRequired = ref(false);
    const updatedLabel = ref("");
    const isError = computed(() => {
      return props.error;
    });
    const dateValue = ref(props.value);
    const isTypeString = ref(false);

    const listRequired = () => {
      if (props.label && props.label.includes("*")) {
        updatedLabel.value = props.label.replace(/\*/g, "");
        isRequired.value = true;
      } else {
        isRequired.value = false;
        updatedLabel.value = props.label;
      }

      if (props.type === "tel") {
        const newValue = `${props.value}`
          .toString()
          .replace("+855", "0")
          .replace(" ", "");
        emit("update:value", newValue);
      }

      if (props.zero && !props.value) {
        emit("update:value", "0");
      }

      if (props.integer && !props.value) {
        const newValue = props.value.toString();
        if (newValue.startsWith("0")) {
          emit("update:value", "1");
        }
      }
    };

    onMounted(() => {
      listRequired();
    });

    function validatePhoneNumber(value: string): string {
      const phonePattern =
        /^[+]?[(]?[0-9]{1,4}[)]?[0-9]{1,4}[-.\s]?([0-9]{1,4}[-.\s]?){1,3}$/;

      if (phonePattern.test(value)) {
        return value.replace(/\s+/g, "");
      } else {
        return value;
      }
    }

    const onEmits = (e: InputEvent) => {
      e.preventDefault();

      let value = (e.target as HTMLInputElement).value;
      const submitValue = ref<any>(value); // Initialize with null or any default value

      const validatedPhoneNumber = validatePhoneNumber(value);
      value = validatedPhoneNumber;

      if (props.space) {
        const inputElement = e.target as HTMLInputElement;

        inputElement.value = inputElement.value.replace(/\s/g, "");
        value = inputElement.value;
      }

      if (
        props.number ||
        (props.type === "number" && !props.number) ||
        props.price ||
        props.decimal
      ) {
        const textWithoutSpaces = value.replace(/\s/g, "");
        const parts = textWithoutSpaces.split(".");

        if (value === "0.00") {
          value = "0";
        }

        if (textWithoutSpaces.startsWith("00")) {
          value = "";
        }

        if (textWithoutSpaces && Number(textWithoutSpaces) > 0) {
          const newVal = Number(textWithoutSpaces);
          value = newVal.toString();
        }

        if (
          textWithoutSpaces.split(".").length - 1 > 1 ||
          (parts.length > 1 && parts[parts.length - 1].length > 2)
        ) {
          value = "";
        }

        if (
          !/^[\d.]+$/.test(textWithoutSpaces) ||
          isNaN(Number(textWithoutSpaces))
        ) {
          value = "";
        } else {
          value = value.slice(0, 30);
        }

        submitValue.value === value ? parseFloat(value) : "";
      }

      if (props.qty) {
        if (
          value.startsWith("-0") ||
          value.startsWith("00") ||
          value.startsWith("-0.00") ||
          (value && !Number(value))
        ) {
          value = "";
        }

        value = value.slice(0, 10);
        submitValue.value === value ? parseFloat(value) : "";
      }

      if (props.version) {
        const regex = /^[0-9.]+$/;
        const match = regex.test(value);

        if (
          (isNaN(parseFloat(value)) && value !== ".") ||
          (value && value.includes("..")) ||
          !match
        ) {
          value = "";
        }
      }

      if (props.latLong) {
        const regex = /^[0-9.]+$/;
        const match = regex.test(value);

        if (
          (isNaN(parseFloat(value)) && value !== ".") ||
          (value === "." && value.includes(".")) ||
          (value && value.includes("..")) ||
          !match
        ) {
          value = "";
        }

        submitValue.value === value ? parseFloat(value) : "";
      }

      if (props.percentage) {
        if (Number(value) > 100 || !Number(value)) {
          value = "0";
        }
      }

      if (props.type === "tel") {
        if (value.startsWith("00")) {
          value = value.replace(/^00/, "0");
        }

        value = value
          .replace("+855", "0")
          .replace("+8550", "0")
          .replaceAll(" ", "")
          .replace(/^\s*/, "");
        value = value.slice(0, 10);

        if (/^[^0]/.test(value)) {
          const newRegex = /^\d{8,9}$/;

          if (value && !newRegex.test(value)) {
            emit("update:value", null);
            if (value && value.startsWith("0")) {
              value = "";
            }
          }
        }
      }

      if (props.integer) {
        const spaceValue = value.replace(/\s/g, "").replaceAll(" ", "");
        value = spaceValue;

        if (!/^\d+$/.test(spaceValue) || isNaN(Number(spaceValue))) {
          value = "";
        }

        submitValue.value === value ? parseFloat(value) : "";
      }

      if (props.lowercase) {
        emit("update:value", value.toLowerCase());
        value = value.toLowerCase();
      }

      if (props.uppercase) {
        emit("update:value", value.toUpperCase());
        value = value.toUpperCase();
      }

      emit("update:value", value);
    };

    const onKeypress = (evt: KeyboardEvent) => {
      const input = evt.target as HTMLInputElement;
      input.value = validatePhoneNumber(input.value);

      if (props.type === "tel") {
        if (input.value.startsWith("00")) {
          input.value = input.value.replace(/^00/, "0");
        }
        input.value = input.value.replace(/^\s*/, "");

        const charCode = evt.which ? evt.which : evt.keyCode;
        if (
          (charCode > 31 &&
            (charCode < 48 || charCode > 57) &&
            charCode !== 46) ||
          input.value.length >= 10
        ) {
          evt.preventDefault();
        }

        emit("update:value", input.value.toString());
      }

      if (props.zero) {
        const newValue = input.value;
        const parsedValue = parseInt(newValue) || 0;

        const charCode = evt.which ? evt.which : evt.keyCode;

        if (
          (charCode > 31 &&
            (charCode < 48 || charCode > 57) &&
            charCode !== 46) ||
          charCode === 46
        ) {
          evt.preventDefault();
        }

        if (input.value.length > 15) {
          evt.preventDefault();
        } else {
          emit("update:value", parsedValue);
          input.value = parsedValue.toString();
        }
      }

      if (props.qty) {
        if (evt) {
          const charCode = evt.which ? evt.which : evt.keyCode;

          if (
            input.value.includes("-0") ||
            (input.value.includes("00") && Number(input.value) === 0) ||
            input.value.includes("-0.00")
          ) {
            input.value = "";
          }

          if (
            (charCode > 31 &&
              (charCode < 48 || charCode > 57) &&
              charCode !== 45) ||
            (input.value.startsWith("-") && charCode === 45)
          ) {
            evt.preventDefault();
          }
        }

        if (input.value.length >= 10) {
          evt.preventDefault();
        } else {
          emit("update:value", input.value.slice(0, 10));
        }
      }

      if (props.price) {
        const newValue = input.value;
        const parsedValue = parseFloat(newValue);

        if (isNaN(parsedValue) || parsedValue < 0) {
          input.value = "0";
          emit("update:value", 0);
        } else {
          emit("update:value", parsedValue);
          input.value = parsedValue.toString();
        }
      }

      if (props.percentage) {
        const charCode = evt.which ? evt.which : evt.keyCode;

        if (
          (charCode > 31 &&
            (charCode < 48 || charCode > 57) &&
            charCode !== 45) ||
          (input.value.length === 2 && parseInt(input.value) > 99) ||
          (input.value.startsWith("0") && parseInt(input.value))
        ) {
          if (
            input.value.startsWith("0") &&
            charCode !== 48 &&
            parseInt(input.value)
          ) {
            emit("update:value", input.value);
            const pasteNumber = parseInt(input.value);
            input.value = pasteNumber.toString();
          } else {
            evt.preventDefault();
          }
        }
      }

      if (props.format) {
        let isValid = true;
        if (typeof props.format === "string") {
          const dynamicRegex = new RegExp(props.format, "i");
          const inputValue = input.value + String.fromCharCode(evt.keyCode);
          isValid = dynamicRegex.test(inputValue);
        } else if (props.format instanceof RegExp) {
          isValid = props.format.test(input.value);
        }
        if (!isValid) {
          evt.preventDefault();
        }
      }

      if (props.length) {
        if (Number(input.value.length) === Number(props.length)) {
          evt.preventDefault();
        }
      }

      if (props.type == "letter") {
        if (!/[a-z0-9]/.test(evt.key)) {
          evt.preventDefault();
        }
      }

      if (input.value.startsWith(" ")) {
        evt.preventDefault();
      }

      if (props.string) {
        if (!/^[a-zA-Z\s]*$/.test(evt.key)) {
          evt.preventDefault();
        }
      }

      if (props.latLong) {
        const charCode = evt.which ? evt.which : evt.keyCode;

        if (
          (isNaN(parseFloat(evt.key)) && evt.key !== ".") ||
          (evt.key === "." && input.value.includes(".")) ||
          (input.value &&
            input.value[input.value.length - 1] === "." &&
            charCode === 46)
        ) {
          evt.preventDefault();
        }
      }

      if (props.version) {
        const charCode = evt.which ? evt.which : evt.keyCode;

        if (
          (isNaN(parseFloat(evt.key)) && evt.key !== ".") ||
          (input.value &&
            input.value[input.value.length - 1] === "." &&
            charCode === 46)
        ) {
          evt.preventDefault();
        }
      }

      if (props.lowercase) {
        emit("update:value", input.value.toLowerCase());
      }

      if (props.uppercase) {
        emit("update:value", input.value.toUpperCase());
      }

      if (props.number || (props.type === "number" && !props.number)) {
        const spaceValue = input.value.replace(/\s/g, "");
        emit("update:value", spaceValue);

        const charCode = evt.which ? evt.which : evt.keyCode;

        const parts = spaceValue.split(".");

        if (
          (spaceValue.includes(".") && charCode === 46) ||
          (parts.length > 1 && parts[parts.length - 1].length > 1) ||
          (!spaceValue && charCode === 46)
        ) {
          if (input.value === "0.00") {
            input.value = "0";
          }
          evt.preventDefault();
        }

        if (
          !/^\d+(\.\d*)?$/.test(spaceValue) ||
          isNaN(parseFloat(spaceValue))
        ) {
          emit("update:value", null);
        }

        if (evt) {
          const charCode = evt.which ? evt.which : evt.keyCode;
          if (
            charCode > 31 &&
            (charCode < 48 || charCode > 57) &&
            charCode !== 46
          ) {
            evt.preventDefault();
          }
        }

        if (input.value.length >= 30) {
          evt.preventDefault();
        } else {
          emit("update:value", input.value.slice(0, 30));
        }
      }

      if (props.integer) {
        const spaceValue = input.value.replace(/\s/g, "");
        emit("update:value", spaceValue);

        if (input.value.startsWith("0") || input.value === "") {
          if (input.value && input.value.length > 1) {
            input.value = input.value.replace(/^0/, "");
            evt.preventDefault();
          }
          input.value = input.value.replace(/^0/, "1");
        }

        if (evt) {
          const charCode = evt.which ? evt.which : evt.keyCode;

          if (
            (charCode > 31 &&
              (charCode < 48 || charCode > 57) &&
              charCode !== 46) ||
            charCode === 46 ||
            charCode === 190
          ) {
            evt.preventDefault();
          }
        }

        if (input.value.length >= 30) {
          evt.preventDefault();
        } else {
          emit("update:value", input.value.slice(0, 30));
        }
      }

      props.schema();
    };

    const validateType = (type: string) => {
      if (type === "letter") {
        return "text";
      }
      return type;
    };

    const onEnter = () => {
      emit("enter");
    };

    const integerRead = (str: string) => {
      const integerPattern = /^-?\d+$/;
      return integerPattern.test(str);
    };

    const copyToClipboard = () => {
      const textArea = document.createElement("textarea");
      let valueToCopy = props.value;

      if (Array.isArray(props.value) || typeof props.value === "object") {
        valueToCopy = JSON.stringify(props.value);
      }

      textArea.value = String(valueToCopy);
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      showPopup.value = true;

      setTimeout(() => {
        showPopup.value = false;
      }, 1500);
    };

    const pasteDecimal = () => {
      const numStr = String(props.value);
      const decimalIndex = numStr.indexOf(".");

      if (decimalIndex === -1 || decimalIndex === numStr.length - 1) {
        return false;
      }

      const decimalPart = numStr.substring(decimalIndex + 1);

      if (decimalPart.length >= 2) {
        return true;
      } else {
        return false;
      }
    };

    const checkDots = (evt: KeyboardEvent) => {
      const dotCount =
        ((evt.target as HTMLInputElement).value &&
          (evt.target as HTMLInputElement).value.match(/\./g)) ||
        [];

      return (dotCount && evt.key === "." && true) || false;
    };

    const isFloat = (value: string) => {
      const floatValue = parseFloat(value);

      if (!isNaN(floatValue) && typeof floatValue === "number") {
        return floatValue % 1 !== 0;
      }

      return false;
    };

    const handleKeyDown = (evt: KeyboardEvent) => {
      const input = evt.target as HTMLInputElement;

      if (evt.ctrlKey || evt.metaKey) {
        const pasteHandler = (pasteEvent: ClipboardEvent) => {
          const clipboardData = pasteEvent.clipboardData;

          if (clipboardData) {
            const pastedText = clipboardData.getData("text");
            emit("update:value", null); // clear input value to paste new value

            if (props.type === "tel") {
              if (input.value.startsWith("00")) {
                input.value = input.value.replace(/^00/, "0");
              }

              input.value = input.value
                .replace("+855", "0")
                .replace(/^\s*/, "");

              const phoneString = String(props.value);
              const phoneRegex = /^0\d{8,9}$/;
              const digitRegex = /^0{2,}/;

              if (phoneString) {
                if (/\D/.test(phoneString)) {
                  emit("update:value", null);
                }

                if (/^[^0]/.test(phoneString)) {
                  const newRegex = /^\d{8,9}$/;

                  if (phoneString && !newRegex.test(phoneString)) {
                    emit("update:value", null);
                  }
                } else {
                  if (
                    (phoneString && !phoneRegex.test(phoneString)) ||
                    (phoneString && digitRegex.test(phoneString))
                  ) {
                    emit("update:value", null);
                  }
                }
              }
            } else if (
              props.number ||
              (props.type === "number" && !props.number)
            ) {
              const textWithoutSpaces = pastedText.replace(/\s/g, "");

              if (
                !/^[\d.]+$/.test(textWithoutSpaces) ||
                isNaN(Number(textWithoutSpaces))
              ) {
                emit("update:value", null);
              }
            } else {
              evt.preventDefault();
            }
          }

          input.removeEventListener("paste", pasteHandler);
        };

        input.addEventListener("paste", pasteHandler);
      }
    };

    const dateOnChange = function () {
      emit("update:value", dateValue.value);
      props.schema();
    };

    return {
      isRequired,
      updatedLabel,
      showPopup,
      autocompleteValue,
      isError,
      onEmits,
      onKeypress,
      validateType,
      onEnter,
      integerRead,
      copyToClipboard,
      pasteDecimal,
      checkDots,
      isFloat,
      handleKeyDown,
      isTypeString,
      dateValue,
      dateOnChange,
    };
  },
});
